import { useNavigate } from "react-router-dom";
import ScannerWidget from "../components/ScannerWidget";
import PricingSection from "../components/PricingSection";
import { FounderSection, ProofSection } from "../components/TrustSections";
import site from "../content/site.json";
import { Button } from "../components/ui/button";
import { Search, ScanLine, Lightbulb, Trophy, MousePointerClick, CreditCard,
  ArrowRight, Check, ShieldCheck, TrendingUp, RefreshCw, Clock } from "lucide-react";

const CAPABILITIES = [
  { n: "1", t: "GET FOUND", d: "Make your business easier for AI systems to discover.", icon: Search },
  { n: "2", t: "GET UNDERSTOOD", d: "Clearly communicate products, services, pricing, availability and policies.", icon: Lightbulb },
  { n: "3", t: "GET CHOSEN", d: "Make your offering easier for AI systems to accurately compare.", icon: Trophy },
  { n: "4", t: "TAKE ACTION", d: "Allow compatible agents to quote, book, reserve, order or access services.", icon: MousePointerClick },
  { n: "5", t: "GET PAID", d: "Enable authorized agent-compatible transaction paths where appropriate.", icon: CreditCard },
];

const COMPARE = [
  ["Human reads webpage", "Agent retrieves structured information"],
  ["Human searches menus", "Agent queries catalog"],
  ["Human asks price", "Agent retrieves price"],
  ["Human requests quote", "Agent invokes quote workflow"],
  ["Human checks availability", "Agent retrieves availability"],
  ["Human books manually", "Agent invokes booking workflow"],
  ["Human checks email", "Agent retrieves order status"],
  ["Human checkout", "Agent-compatible transaction path"],
  ["SEO", "SEO + AEO/GEO + AI discovery"],
  ["Website navigation", "Website + APIs + agent interfaces"],
];

const STEPS = [
  ["Scan", "Enter your URL."],
  ["Score", "Receive your AI Revenue Readiness Score."],
  ["Diagnose", "See where AI systems may encounter friction."],
  ["Choose", "Receive the implementation plan recommended for your business."],
  ["We Implement", "Our autonomous implementation system performs authorized fixes."],
  ["We Test", "Every relevant capability is re-tested."],
  ["Get Verified", "Qualifying businesses receive AgenticReady Verified™."],
  ["Stay Ready", "Continuous monitoring detects breakage and new opportunities."],
];

const DEMO = { "AI Visibility": 71, "Business Understanding": 64, "Structured Data": 48, "Agent Discovery": 39, "Transaction Readiness": 21, "Payment Readiness": 17 };

export default function Home() {
  const navigate = useNavigate();

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-950 via-indigo-950 to-slate-950 text-white">
        <div className="absolute inset-0 grid-dots-dark opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <p className="font-mono text-xs uppercase tracking-widest text-indigo-300 mb-5 animate-fade-up">{site.hero.eyebrow}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl animate-fade-up" style={{ animationDelay: "0.05s" }} data-testid="hero-headline">
            {site.hero.headline} <span className="text-indigo-400">{site.hero.headline_accent}</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed animate-fade-up" style={{ animationDelay: "0.1s" }}>
            AI is changing how customers search, compare, choose and buy. AgenticReady scans your existing website to identify what may prevent AI systems and autonomous agents from understanding your business—and helps implement the infrastructure to make your company ready for AI-mediated commerce.
          </p>
          <div className="mt-9 animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <ScannerWidget variant="hero" />
          </div>
          <p className="mt-8 text-sm text-slate-400 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Keep your current website. Upgrade what AI can understand and do with it.</p>
          <div className="mt-6 inline-flex items-center gap-3 rounded-full border border-emerald-400/30 bg-emerald-400/10 pl-2 pr-4 py-2 backdrop-blur-sm" data-testid="hero-verified-badge">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-400/20">
              <ShieldCheck className="h-4 w-4 text-emerald-300" />
            </span>
            <span className="text-sm font-semibold text-emerald-200">Make My Business AI Ready Verified&trade;</span>
            <span className="hidden sm:inline text-xs text-emerald-300/70 border-l border-emerald-400/30 pl-3">Score 90+ certified for AI commerce</span>
          </div>
        </div>
      </section>

      {/* MARKET PROOF */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 max-w-3xl">AI Commerce Is Moving From Experiment to Infrastructure.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          {[
            ["$3–$5 TRILLION", "Potential global consumer commerce mediated by AI agents by 2030 under published moderate scenarios."],
            ["109M+ TRANSACTIONS", "The x402 machine-payment protocol has already processed more than one hundred million transactions."],
            ["1 IN 10+", "More than one in ten online shoppers could routinely use AI agents to shop and pay by 2030."],
          ].map(([stat, copy]) => (
            <div key={stat} className="rounded-2xl border border-slate-200 bg-white p-7">
              <p className="font-heading text-3xl font-bold text-indigo-600">{stat}</p>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{copy}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-slate-500 mt-8 max-w-3xl">OpenAI, Stripe, Google, Visa, Mastercard, Coinbase, Cloudflare and others are already building infrastructure for AI-mediated commerce.</p>
        <button onClick={() => navigate("/free-scan")} className="mt-4 text-indigo-600 font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="marketproof-cta">See If Your Business Is Ready <ArrowRight className="h-4 w-4" /></button>
      </section>

      {/* PROBLEM + COMPARISON */}
      <section className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold max-w-3xl">A Great Website Can Still Be Difficult for an AI Agent to Buy From.</h2>
          <p className="mt-5 text-slate-300 max-w-2xl">Most websites were designed around a human: look at a page, click a menu, fill a form, call for pricing. AI agents work differently — they prefer structured business information, clear prices and policies, reliable data, and transaction paths they can safely access.</p>
          <div className="mt-10 rounded-2xl border border-slate-800 overflow-hidden">
            <div className="grid grid-cols-2 bg-slate-900 text-xs font-mono uppercase tracking-wider">
              <div className="px-5 py-3 text-slate-400 border-r border-slate-800">Traditional Website</div>
              <div className="px-5 py-3 text-indigo-300">Agentic-Ready Business</div>
            </div>
            {COMPARE.map(([a, b], i) => (
              <div key={i} className={`grid grid-cols-2 text-sm ${i % 2 ? "bg-slate-900/40" : ""}`}>
                <div className="px-5 py-3 text-slate-400 border-r border-slate-800">{a}</div>
                <div className="px-5 py-3 text-white flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />{b}</div>
              </div>
            ))}
          </div>
          <Button onClick={() => navigate("/free-scan")} className="mt-8 h-12 px-6 rounded-full bg-orange-500 hover:bg-orange-600 font-semibold" data-testid="problem-cta">Test My Website</Button>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">What Does "Agentic Ready" Mean?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-5 mt-10">
          {CAPABILITIES.map((c) => (
            <div key={c.n} className="rounded-2xl border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:shadow-lg transition-all">
              <div className="h-11 w-11 rounded-xl bg-indigo-50 flex items-center justify-center mb-4"><c.icon className="h-5 w-5 text-indigo-600" /></div>
              <p className="font-mono text-xs text-slate-400">{c.n}</p>
              <h3 className="font-heading font-bold text-slate-900 mt-1">{c.t}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SCANNER DEMO */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">See What AI Sees.</h2>
            <p className="mt-4 text-slate-600 max-w-md">A sample AI Revenue Readiness Score™ for a typical small business website.</p>
            <div className="mt-6 rounded-xl bg-white border border-slate-200 p-5">
              <p className="text-sm font-semibold text-slate-900">Your prices are visible to people but difficult for automated systems to reliably retrieve.</p>
              <p className="text-sm text-slate-500 mt-2">An AI shopping or purchasing agent may be unable to confidently compare or complete the buying journey.</p>
            </div>
            <Button onClick={() => navigate("/free-scan")} className="mt-6 h-12 px-6 rounded-full bg-indigo-600 hover:bg-indigo-700 font-semibold" data-testid="demo-scan-cta">Scan My Real Site</Button>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-7 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm text-slate-500">examplecompany.com</span>
              <span className="text-xs font-mono font-semibold px-2.5 py-1 rounded-full bg-rose-50 text-rose-600 border border-rose-200">HIGH RISK</span>
            </div>
            <div className="mt-4 flex items-end gap-2">
              <span className="font-heading text-5xl font-bold text-rose-500">42</span>
              <span className="text-slate-400 mb-1.5 font-mono">/ 100</span>
            </div>
            <p className="text-xs text-slate-400 font-mono uppercase tracking-wide mt-1">AI Revenue Readiness Score™</p>
            <div className="mt-6 space-y-3">
              {Object.entries(DEMO).map(([k, v]) => (
                <div key={k}>
                  <div className="flex justify-between text-xs mb-1"><span className="text-slate-600">{k}</span><span className="font-mono text-slate-500">{v}</span></div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${v}%`, background: v >= 60 ? "#f59e0b" : "#f43f5e" }} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">From Traditional Website to Agentic Ready.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {STEPS.map(([t, d], i) => (
            <div key={t} className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="h-9 w-9 rounded-lg bg-slate-900 text-white flex items-center justify-center font-heading font-bold text-sm">{i + 1}</div>
              <h3 className="font-heading font-semibold text-slate-900 mt-4">{t}</h3>
              <p className="text-sm text-slate-500 mt-1.5">{d}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10"><Button onClick={() => navigate("/free-scan")} className="h-12 px-7 rounded-full bg-indigo-600 hover:bg-indigo-700 font-semibold" data-testid="how-cta">Get My Free Score</Button></div>
      </section>

      {/* COST OF INACTION / ROI */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8 sm:p-12">
          <div className="max-w-2xl">
            <p className="font-mono text-xs uppercase tracking-widest text-orange-500 mb-3">The math most businesses miss</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">The Setup Fee Isn't the Real Number.</h2>
            <p className="mt-5 text-slate-600">The real cost is every AI-driven customer who searches, compares and buys — and never sees your business because an AI agent couldn't read it. A single missed customer a month can exceed the entire cost of getting ready.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-5 mt-10">
            {[
              [TrendingUp, "Pays for itself", "If AI readiness earns you one extra customer a month, it typically covers its own cost."],
              [Clock, "Working 24/7", "AI assistants recommend businesses around the clock. You're either in the answer — or you're not."],
              [Trophy, "First-mover edge", "Most competitors haven't done this yet. Getting ready now is the cheapest it will ever be."],
            ].map(([Icon, t, d]) => (
              <div key={t} className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="h-11 w-11 rounded-xl bg-orange-50 flex items-center justify-center mb-4"><Icon className="h-5 w-5 text-orange-500" /></div>
                <h3 className="font-heading font-bold text-slate-900">{t}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Start Where Your Business Is Today.</h2>
          </div>
          <div className="mt-12"><PricingSection /></div>
          <p className="text-center text-slate-500 mt-10 max-w-xl mx-auto">Not sure? Don't guess. Scan your website and we'll recommend the appropriate level automatically.</p>
          <div className="text-center mt-4"><Button variant="outline" onClick={() => navigate("/free-scan")} className="rounded-full" data-testid="pricing-scan-cta">Scan Free</Button></div>
        </div>
      </section>

      <FounderSection />
      <ProofSection />

      {/* GUARANTEE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="max-w-2xl">
          <p className="font-mono text-xs uppercase tracking-widest text-indigo-600 mb-3">Our commitment to you</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Backed by Real, Documented Work — Not Promises.</h2>
          <p className="mt-5 text-slate-600">You pay for concrete deliverables, verified by an independent QA pass, with a measurable before-and-after AI Revenue Readiness Score™. If your score doesn't improve after implementation, we keep working until it does — no extra setup cost.</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-5 mt-10">
          {[
            [TrendingUp, "Measurable result", "A before-and-after readiness score shows exactly what changed on your site."],
            [ShieldCheck, "Independent QA pass", "Every deliverable is quality-checked before it ships — no unchecked work."],
            [RefreshCw, "No lock-in", "Cancel monthly monitoring anytime, per your subscription terms."],
          ].map(([Icon, t, d]) => (
            <div key={t} className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="h-11 w-11 rounded-xl bg-emerald-50 flex items-center justify-center mb-4"><Icon className="h-5 w-5 text-emerald-600" /></div>
              <h3 className="font-heading font-bold text-slate-900">{t}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-6">
          <p className="text-sm font-semibold text-slate-700 mb-3">Every engagement includes:</p>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {["Initial score","Detailed findings","Documented implementation","Independent QA pass","Before/after score","Remaining recommendations","Ongoing monitoring","Secure customer portal"].map((f) => (
              <li key={f} className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3"><Check className="h-4 w-4 text-emerald-500 shrink-0" /><span className="text-sm text-slate-700 font-medium">{f}</span></li>
            ))}
          </ul>
        </div>
        <p className="mt-6 text-xs text-slate-400 max-w-3xl leading-relaxed">Disclaimer: We do not control and cannot guarantee that ChatGPT, Google, Gemini or any other third-party AI system will rank, recommend or transact with your business. We improve the infrastructure those systems rely on to discover, understand and transact with businesses.</p>
      </section>

      {/* FINAL CTA */}
      <section className="bg-gradient-to-b from-indigo-950 to-slate-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">The Internet Was Built for Humans.<br /><span className="text-indigo-400">Commerce Is Being Rebuilt for AI.</span></h2>
          <p className="mt-6 text-slate-300 max-w-2xl mx-auto">Your business does not necessarily need another website. It needs digital infrastructure that helps tomorrow's AI customers find it, understand it and transact with it.</p>
          <div className="mt-9 flex justify-center"><ScannerWidget variant="dark" buttonLabel="Find Out If I'm Ready" /></div>
        </div>
      </section>
    </div>
  );
}
